<template>
  <modal
    transition="nice-modal-fade"
    width="720"
    height="auto"
    :adaptive="true"
    :reset="true"
    :click-to-close="false"
    :name="modalName"
    class="listing--form-modal action--modal recommended-change-vmodal"
    @opened="opened"
    @before-open="beforeOpen"
  >
    <div class="modal--wrapper">
      <section-loading :show="loading" />

      <!-- Header -->
      <header class="modal--header">
        <div class="modal--header-content">
          <div class="modal--header-icon">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M5 19V10.5L12 5L19 10.5V19H14.5V14H9.5V19H5Z"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />

              <path
                d="M16.5 4.5L17.18 5.88L18.7 6.1L17.6 7.17L17.86 8.68L16.5 7.97L15.14 8.68L15.4 7.17L14.3 6.1L15.82 5.88L16.5 4.5Z"
                fill="currentColor"
              />
            </svg>
          </div>

          <div class="modal--header-copy">
            <div class="modal--eyebrow">Listing Rekomendasi</div>

            <h2 class="modal--title">
              {{ $t('listing.modal.changeRecommended.title') }}
            </h2>

            <p class="modal--description">
              Pilih listing aktif dengan detail yang sama untuk menggantikan listing rekomendasi
              saat ini.
            </p>
          </div>
        </div>

        <button type="button" class="modal--close" aria-label="Tutup modal" @click="close">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M6 6L18 18M18 6L6 18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </header>

      <!-- Step indicator -->
      <div class="modal--steps">
        <div
          class="modal--step"
          :class="{
            'modal--step-active': step === 1,
            'modal--step-complete': step > 1,
          }"
        >
          <div class="modal--step-number">
            <svg
              v-if="step > 1"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M5 12.5L9.5 17L19 7.5"
                stroke="currentColor"
                stroke-width="2.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            <span v-else>1</span>
          </div>

          <div class="modal--step-copy">
            <span class="modal--step-label"> Langkah 1 </span>

            <strong>Pilih listing</strong>
          </div>
        </div>

        <div class="modal--step-line" :class="{ active: step === 2 }" />

        <div class="modal--step" :class="{ 'modal--step-active': step === 2 }">
          <div class="modal--step-number">
            <span>2</span>
          </div>

          <div class="modal--step-copy">
            <span class="modal--step-label"> Langkah 2 </span>

            <strong>Konfirmasi</strong>
          </div>
        </div>
      </div>

      <!-- Scrollable body -->
      <main ref="modalBody" class="modal--body">
        <!-- Step 1 -->
        <section v-if="step === 1" class="change--content">
          <div class="period--card">
            <div class="period--icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect
                  x="3"
                  y="5"
                  width="18"
                  height="16"
                  rx="3"
                  stroke="currentColor"
                  stroke-width="1.8"
                />

                <path
                  d="M8 3V7M16 3V7M3 10H21"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
              </svg>
            </div>

            <div class="period--copy">
              <span class="period--label"> Periode rekomendasi </span>

              <strong class="period--value">
                {{ formattedPeriod }}
              </strong>
            </div>
          </div>

          <!-- Listing selector -->
          <div class="listing--form-group">
            <div class="listing--form-heading">
              <label for="selectedListing" class="listing--form-label"> Pilih Listing Baru </label>
            </div>

            <multiselect
              id="selectedListing"
              v-model="selectedListing"
              :options="availableListings"
              :close-on-select="true"
              :searchable="true"
              :internal-search="false"
              :allow-empty="false"
              :show-labels="false"
              :multiple="false"
              :loading="loading"
              :max-height="260"
              track-by="uuid"
              label="title"
              placeholder="Cari berdasarkan listing id atau judul listing"
              class="bulk--repost-input"
              @search-change="searchListings"
            >
              <!-- Selected listing -->
              <template slot="singleLabel">
                <div
                  v-if="selectedListing"
                  class="bulk--repost-option bulk--repost-option-selected"
                >
                  <div class="bulk--repost-img">
                    <img :src="selectedListing.primary_photo" :alt="selectedListing.title" />
                  </div>

                  <div class="bulk--repost-content">
                    <div class="bulk--repost-top">
                      <div class="bulk--repost-title">
                        {{ selectedListing.title }}
                      </div>

                      <div v-if="selectedListing.property_type" class="property--type">
                        {{ selectedListing.property_type }}
                      </div>
                    </div>

                    <div class="bulk--repost-meta">
                      {{ `${$t('general.listingNumber')}: ${selectedListing.listing_number}` }}
                    </div>

                    <div class="bulk--repost-price">
                      {{ selectedListing.price_str }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- Dropdown option -->
              <template slot="option" slot-scope="{ option }">
                <div class="bulk--repost-option">
                  <div class="bulk--repost-img">
                    <img :src="option.primary_photo" :alt="option.title" />
                  </div>

                  <div class="bulk--repost-content">
                    <div class="bulk--repost-top">
                      <div class="bulk--repost-title">
                        {{ option.title }}
                      </div>

                      <div v-if="option.property_type" class="property--type">
                        {{ option.property_type }}
                      </div>
                    </div>

                    <div class="bulk--repost-meta">
                      {{ `${$t('general.listingNumber')}: ${option.listing_number}` }}
                    </div>

                    <div class="bulk--repost-price">
                      {{ option.price_str }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- Empty state -->
              <template slot="noOptions">
                <div class="multiselect--empty">
                  <div class="multiselect--empty-icon">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M4 19V10.5L12 4L20 10.5V19H15V14H9V19H4Z"
                        stroke="currentColor"
                        stroke-width="1.7"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>

                  <span>
                    {{ $t('recommendedListing.emptyChangeListing') }}
                  </span>
                </div>
              </template>

              <template slot="noResult">
                <div class="multiselect--empty">
                  <div class="multiselect--empty-icon">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M10.5 18C14.6421 18 18 14.6421 18 10.5C18 6.35786 14.6421 3 10.5 3C6.35786 3 3 6.35786 3 10.5C3 14.6421 6.35786 18 10.5 18Z"
                        stroke="currentColor"
                        stroke-width="1.7"
                      />

                      <path
                        d="M16 16L21 21"
                        stroke="currentColor"
                        stroke-width="1.7"
                        stroke-linecap="round"
                      />
                    </svg>
                  </div>

                  <span>Listing tidak ditemukan.</span>
                </div>
              </template>
            </multiselect>
          </div>

          <div class="information--notice">
            <div class="information--notice-icon">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />

                <path
                  d="M12 10.5V16"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />

                <circle cx="12" cy="7.5" r="1" fill="currentColor" />
              </svg>
            </div>

            <p>
              Hanya listing aktif dengan tipe listing, tipe properti, provinsi, kota, dan area yang
              sama yang dapat dipilih. Listing pengganti akan menggunakan sisa periode rekomendasi
              yang sedang berjalan.
            </p>
          </div>
        </section>

        <!-- Step 2 -->
        <section v-else class="confirmation--content">
          <div class="confirmation--heading">
            <div class="confirmation--icon">
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M5 12.5L9.5 17L19 7.5"
                  stroke="currentColor"
                  stroke-width="2.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>

            <div class="confirmation--heading-copy">
              <h3>
                {{ $t('listing.modal.changeRecommended.confirm') }}
              </h3>

              <p>Pastikan listing dan informasi berikut sudah benar sebelum melanjutkan.</p>
            </div>
          </div>

          <div v-if="selectedListing" class="selected--listing-card">
            <div class="selected--listing-image">
              <img :src="selectedListing.primary_photo" :alt="selectedListing.title" />
            </div>

            <div class="selected--listing-content">
              <div class="selected--listing-label">Listing pengganti</div>

              <h4>
                {{ selectedListing.title }}
              </h4>

              <div class="selected--listing-number">
                {{ `${$t('general.listingNumber')}: ${selectedListing.listing_number}` }}
              </div>

              <div class="selected--listing-bottom">
                <strong>
                  {{ selectedListing.price_str }}
                </strong>

                <span v-if="selectedListing.property_type">
                  {{ selectedListing.property_type }}
                </span>
              </div>
            </div>
          </div>

          <div class="summary--card">
            <div class="summary--header">
              <h4>Ringkasan perubahan</h4>
              <span>Detail penggunaan</span>
            </div>

            <div class="summary--row">
              <div class="summary--label">
                {{ $t('listing.modal.changeRecommended.amount') }}
              </div>

              <div class="summary--value">
                {{ `1 ${$t('listing.modal.changeRecommended.slot')}` }}
              </div>
            </div>

            <div class="summary--divider" />

            <div class="summary--row">
              <div class="summary--label">
                {{ $t('listing.modal.changeRecommended.usedCoin') }}
              </div>

              <div class="summary--value">
                {{ `0 ${$t('general.coin')}` }}
              </div>
            </div>

            <div class="summary--divider" />

            <div class="summary--row summary--row-muted">
              <div class="summary--label">
                {{ $t('listing.modal.changeRecommended.yourCoin') }}
              </div>

              <div class="summary--value">
                {{ totalCoins }}
              </div>
            </div>

            <div class="summary--divider" />

            <div class="summary--row summary--row-muted">
              <div class="summary--label">Periode aktif</div>

              <div class="summary--value summary--value-period">
                {{ formattedPeriod }}
              </div>
            </div>
          </div>

          <div class="confirmation--notice">
            Pastikan detail listing pengganti sudah sama dengan listing rekomendasi aktif. Perubahan
            ini tidak menambah atau memperpanjang periode rekomendasi yang sedang berjalan.
          </div>
        </section>
      </main>

      <!-- Footer -->
      <footer class="modal--footer">
        <button
          type="button"
          class="modal--button modal--button-secondary"
          :disabled="loading"
          @click="handleSecondaryAction"
        >
          <svg
            v-if="step === 2"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M19 12H5M11 18L5 12L11 6"
              stroke="currentColor"
              stroke-width="1.9"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          <span>
            {{ step === 2 ? $t('listing.modal.changeRecommended.back') : $t('general.cancel') }}
          </span>
        </button>

        <button
          type="button"
          class="modal--button modal--button-primary"
          :disabled="loading || !selectedListing"
          @click="changeRecommended"
        >
          <span>
            {{ step === 1 ? 'Lanjutkan' : $t('listing.modal.changeRecommended.btn') }}
          </span>

          <svg
            v-if="step === 1"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M5 12H19M13 6L19 12L13 18"
              stroke="currentColor"
              stroke-width="1.9"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          <svg
            v-else
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M5 12.5L9.5 17L19 7.5"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </footer>
    </div>
  </modal>
</template>

<script>
import debounce from 'lodash/debounce';
import { mapState } from 'vuex';
import Multiselect from 'vue-multiselect';
import HelperMixin from '@/mixins/helpers';

const SectionLoading = () => import('@/components/content-loading/section-loading');

export default {
  name: 'change-recommended-modal',

  components: {
    Multiselect,
    SectionLoading,
  },

  mixins: [HelperMixin],

  data() {
    return {
      modalName: 'change-recommended-modal',
      submitAction: null,
      step: 1,
      selectedListing: null,
      listing: null,
      entry: null,
      submitting: false,
      listingSearchQuery: '',
    };
  },

  beforeDestroy() {
    if (this.searchListings && this.searchListings.cancel) {
      this.searchListings.cancel();
    }

    this.showTawkWidget();
  },

  computed: {
    ...mapState({
      coinsLoading: (state) => state.coins.loading,
      recommendedLoading: (state) => state.recommendedListing.loading,
      totalCoins: (state) => state.coins.totalCoins,
      listings: (state) => state.listing.listings,
    }),

    loading() {
      return this.coinsLoading || this.recommendedLoading || this.submitting;
    },

    availableListings() {
      if (Array.isArray(this.listings)) {
        return this.listings;
      }

      if (this.listings && Array.isArray(this.listings.data)) {
        return this.listings.data;
      }

      return [];
    },

    formattedPeriod() {
      if (!this.entry) {
        return '-';
      }

      const startDate = this.entry.start_at
        ? this.$date.format(this.entry.start_at, 'd MMM yyyy')
        : '-';

      const endDate = this.entry.end_at ? this.$date.format(this.entry.end_at, 'd MMM yyyy') : '-';

      return `${startDate} - ${endDate}`;
    },
  },

  methods: {
    async getListings(query = '') {
      this.listingSearchQuery = query || '';
      this.$store.commit('listing/SET_SEARCH_QUERY', this.listingSearchQuery);

      this.$store.commit('listing/SET_CATEGORY', {
        id: 0,
        name: 'Aktif',
        value: 'active',
      });

      await this.$store.dispatch('listing/getAllListings', {
        page: 1,
        useLoading: false,
        forBulkRepost: true,
        eligibleBoostUuid: this.entry ? this.entry.uuid : null,
      });
    },

    searchListings: debounce(function (query) {
      this.getListings(query);
    }, 350),

    close() {
      this.showTawkWidget();
      this.$modal.hide(this.modalName);
    },

    beforeOpen(event) {
      this.hideTawkWidget();
      const params = event.params || {};

      this.submitAction = typeof params.submitAction === 'function' ? params.submitAction : null;

      this.listing = params.listing || null;
      this.entry = params.entry || null;
      this.selectedListing = null;
      this.listingSearchQuery = '';
      this.step = 1;
      this.submitting = false;
    },

    async opened() {
      try {
        await this.$store.dispatch('coins/getTotalCoins');

        await this.getListings();

        this.$store.commit('listing/CLEAR_SELECTED_LISTINGS');

        this.scrollBodyToTop();
      } catch (error) {
        console.error('Error opening modal:', error);

        this.close();
      }
    },

    hideTawkWidget() {
      if (typeof document !== 'undefined') {
        document.body.classList.add('hide-tawk-widget');
      }

      if (typeof window !== 'undefined' && window.Tawk_API && window.Tawk_API.hideWidget) {
        window.Tawk_API.hideWidget();
      }
    },

    showTawkWidget() {
      if (typeof document !== 'undefined') {
        document.body.classList.remove('hide-tawk-widget');
      }

      if (typeof window !== 'undefined' && window.Tawk_API && window.Tawk_API.showWidget) {
        window.Tawk_API.showWidget();
      }
    },

    scrollBodyToTop() {
      this.$nextTick(() => {
        if (this.$refs.modalBody) {
          this.$refs.modalBody.scrollTop = 0;
        }
      });
    },

    setStep(step) {
      this.step = step;
      this.scrollBodyToTop();
    },

    handleSecondaryAction() {
      if (this.step === 2) {
        this.setStep(1);
        return;
      }

      this.close();
    },

    validate() {
      return Boolean(this.selectedListing);
    },

    async changeRecommended() {
      if (!this.validate()) {
        return;
      }

      if (this.step === 1) {
        this.setStep(2);
        return;
      }

      try {
        this.submitting = true;
        this.$emit('submit', this.selectedListing);

        if (typeof this.submitAction === 'function') {
          await this.submitAction(this.selectedListing);
        }

        this.close();
        await this.$swal(
          this.$i18n.t('general.success'),
          this.$i18n.t('listing.modal.changeRecommended.success'),
          'success',
        );
      } catch (error) {
        console.error('ERROR CHANGE RECOMMENDED LISTING: ', error);

        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Gagal mengganti Listing Rekomendasi.';

        await this.$swal(this.$i18n.t('general.failed'), message, 'error');
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../assets/scss/utils/variables';

/*
 * Menimpa ukuran bawaan vue-js-modal agar aman pada mobile.
 */
::v-deep .listing--form-modal .vm--modal {
  overflow: visible;
  border-radius: 22px;
  background: transparent;
  box-shadow: none;
}

::v-deep .listing--form-modal .vm--overlay {
  background: rgba(21, 24, 31, 0.52);
  backdrop-filter: blur(2px);
}

.modal--wrapper {
  --modal-red: #d71920;
  --modal-red-dark: #ad1117;
  --modal-red-soft: #fff5f5;
  --modal-text: #171717;
  --modal-text-secondary: #626262;
  --modal-text-muted: #8b8b8b;
  --modal-border: #e5e5e5;
  --modal-surface: #ffffff;
  --modal-surface-soft: #f7f7f8;

  position: relative;
  display: flex;
  width: 100%;
  max-width: 100%;
  max-height: calc(100dvh - 32px);
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(17, 17, 17, 0.06);
  border-radius: 22px;
  background: var(--modal-surface);
  box-shadow: 0 28px 70px rgba(17, 24, 39, 0.18), 0 8px 24px rgba(17, 24, 39, 0.08);
}

/* Header */

.modal--header {
  position: relative;
  z-index: 4;
  display: flex;
  min-width: 0;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 26px 28px 22px;
  border-bottom: 1px solid var(--modal-border);
  background: #ffffff;
}

.modal--header-content {
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: flex-start;
  gap: 16px;
}

.modal--header-icon {
  display: flex;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(215, 25, 32, 0.14);
  border-radius: 14px;
  color: var(--modal-red);
  background: var(--modal-red-soft);
}

.modal--header-copy {
  min-width: 0;
  flex: 1;
}

.modal--eyebrow {
  margin-bottom: 4px;
  color: var(--modal-red);
  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.modal--title {
  margin: 0;
  color: var(--modal-text);
  font-family: Poppins-Bold, Poppins, sans-serif;
  font-size: 23px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.35px;
  overflow-wrap: anywhere;
}

.modal--description {
  max-width: 510px;
  margin: 6px 0 0;
  color: var(--modal-text-secondary);
  font-family: Poppins-Regular, Poppins, sans-serif;
  font-size: 13px;
  line-height: 1.65;
  overflow-wrap: anywhere;
}

.modal--close {
  display: inline-flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid var(--modal-border);
  border-radius: 11px;
  color: #555555;
  background: #ffffff;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease,
    transform 0.2s ease;
}

.modal--close:hover {
  border-color: rgba(215, 25, 32, 0.22);
  color: var(--modal-red);
  background: var(--modal-red-soft);
  transform: rotate(4deg);
}

.modal--close:focus-visible {
  outline: 3px solid rgba(215, 25, 32, 0.15);
  outline-offset: 2px;
}

/* Steps */

.modal--steps {
  position: relative;
  z-index: 3;
  display: grid;
  grid-template-columns:
    minmax(0, max-content)
    72px
    minmax(0, max-content);
  flex: 0 0 auto;
  align-items: center;
  justify-content: start;
  padding: 16px 28px;
  border-bottom: 1px solid var(--modal-border);
  background: #fafafa;
}

.modal--step {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
  color: var(--modal-text-muted);
}

.modal--step-number {
  display: flex;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  border: 1px solid #d7d7d7;
  border-radius: 50%;
  color: #777777;
  background: #ffffff;
  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 12px;
  transition: color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
}

.modal--step-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.modal--step-label {
  margin-bottom: 1px;
  font-family: Poppins-Regular, Poppins, sans-serif;
  font-size: 10px;
  line-height: 1.3;
}

.modal--step-copy strong {
  color: inherit;
  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}

.modal--step-active,
.modal--step-complete {
  color: var(--modal-text);
}

.modal--step-active .modal--step-number,
.modal--step-complete .modal--step-number {
  border-color: var(--modal-red);
  color: #ffffff;
  background: var(--modal-red);
}

.modal--step-line {
  width: calc(100% - 20px);
  height: 1px;
  justify-self: center;
  background: #dcdcdc;
  transition: background-color 0.2s ease;
}

.modal--step-line.active {
  background: var(--modal-red);
}

/* Body */

.modal--body {
  position: relative;
  z-index: 2;
  min-width: 0;
  min-height: 0;
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 26px 28px 30px;
  overscroll-behavior: contain;
  scrollbar-color: #c9c9c9 transparent;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}

.modal--body::-webkit-scrollbar {
  width: 7px;
}

.modal--body::-webkit-scrollbar-track {
  background: transparent;
}

.modal--body::-webkit-scrollbar-thumb {
  border: 2px solid transparent;
  border-radius: 999px;
  background: #c9c9c9;
  background-clip: padding-box;
}

.change--content,
.confirmation--content {
  width: 100%;
  min-width: 0;
}

/* Period */

.period--card {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 14px 16px;
  border: 1px solid var(--modal-border);
  border-radius: 14px;
  background: var(--modal-surface-soft);
}

.period--icon {
  display: flex;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: var(--modal-red);
  background: #ffffff;
  box-shadow: 0 3px 10px rgba(17, 24, 39, 0.06);
}

.period--copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.period--label {
  margin-bottom: 2px;
  color: var(--modal-text-muted);
  font-family: Poppins-Regular, Poppins, sans-serif;
  font-size: 11px;
  line-height: 1.4;
}

.period--value {
  color: var(--modal-text);
  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

/* Listing form */

.listing--form-group {
  display: block;
  width: 100%;
  min-width: 0;
  margin: 0 0 18px;
}

.listing--form-heading {
  display: block;
  width: 100%;
  margin-bottom: 14px;
}

.listing--form-label {
  display: block;
  margin: 0 0 5px;
  color: var(--modal-text);
  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
}

.listing--form-hint {
  display: block;
  max-width: 100%;
  margin: 0;
  color: var(--modal-text-muted);
  font-family: Poppins-Regular, Poppins, sans-serif;
  font-size: 11px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.bulk--repost-input {
  position: relative;
  z-index: 5;
  display: block;
  width: 100% !important;
  max-width: none !important;
  min-width: 0;
  margin: 0;
}

/* Multiselect */

.bulk--repost-input::v-deep .multiselect {
  width: 100%;
  min-width: 0;
  min-height: 112px;
}

.bulk--repost-input::v-deep .multiselect__tags {
  display: flex;
  width: 100%;
  min-width: 0;
  min-height: 112px;
  align-items: center;
  padding: 12px 52px 12px 12px;
  overflow: hidden;
  border: 1px solid #dedede;
  border-radius: 15px;
  background: #ffffff;
  box-shadow: none;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.bulk--repost-input::v-deep .multiselect--active {
  z-index: 20;
}

.bulk--repost-input::v-deep .multiselect--active .multiselect__tags {
  border-color: rgba(215, 25, 32, 0.55);
  box-shadow: 0 0 0 4px rgba(215, 25, 32, 0.08);
}

.bulk--repost-input::v-deep .multiselect__placeholder {
  display: flex;
  min-height: 86px;
  align-items: center;
  margin: 0;
  padding: 0 10px;
  color: #969696;
  font-family: Poppins-Regular, Poppins, sans-serif;
  font-size: 13px;
}

.bulk--repost-input::v-deep .multiselect__input {
  width: 100% !important;
  min-height: 86px;
  margin: 0;
  padding: 0 10px;
  color: var(--modal-text);
  font-family: Poppins-Regular, Poppins, sans-serif;
  font-size: 13px;
  line-height: 1.5;
}

.bulk--repost-input::v-deep .multiselect__single {
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  overflow: hidden;
  background: transparent;
}

.bulk--repost-input::v-deep .multiselect__select {
  top: 50%;
  right: 8px;
  width: 40px;
  height: 40px;
  padding: 0;
  transform: translateY(-50%);
}

.bulk--repost-input::v-deep .multiselect__select::before {
  top: 50%;
  margin-top: -2px;
  border-color: #777777 transparent transparent;
}

.bulk--repost-input::v-deep .multiselect__content-wrapper {
  top: calc(100% + 8px);
  width: 100%;
  min-width: 0;
  max-height: 260px !important;
  overflow-x: hidden;
  overflow-y: auto;
  border: 1px solid var(--modal-border);
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 20px 45px rgba(17, 24, 39, 0.14), 0 4px 12px rgba(17, 24, 39, 0.06);
  -webkit-overflow-scrolling: touch;
}

.bulk--repost-input::v-deep .multiselect__content {
  width: 100%;
  min-width: 100%;
}

.bulk--repost-input::v-deep .multiselect__option {
  min-width: 0;
  min-height: 0;
  padding: 8px;
  color: inherit;
  background: #ffffff;
  white-space: normal;
}

.bulk--repost-input::v-deep .multiselect__option::after {
  display: none;
}

.bulk--repost-input::v-deep .multiselect__option--highlight {
  color: inherit;
  background: #f6f6f7;
}

.bulk--repost-input::v-deep .multiselect__option--selected {
  color: inherit;
  background: var(--modal-red-soft);
}

.bulk--repost-input::v-deep .multiselect__option--selected.multiselect__option--highlight {
  color: inherit;
  background: #fdebec;
}

/* Listing option */

.bulk--repost-option {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 13px;
  padding: 5px;
  overflow: hidden;
  border-radius: 10px;
}

.bulk--repost-option-selected {
  padding: 0;
}

.bulk--repost-img {
  width: 78px;
  height: 78px;
  flex: 0 0 78px;
  overflow: hidden;
  border: 1px solid var(--modal-border);
  border-radius: 11px;
  background: #f1f1f1;
}

.bulk--repost-img img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.bulk--repost-content {
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.bulk--repost-top {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.bulk--repost-title {
  display: -webkit-box;
  min-width: 0;
  overflow: hidden;
  color: var(--modal-text);
  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.45;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.property--type {
  max-width: 130px;
  flex: 0 0 auto;
  overflow: hidden;
  padding: 4px 8px;
  border: 1px solid #e3e3e3;
  border-radius: 999px;
  color: #555555;
  background: #f7f7f7;
  font-family: Poppins-Medium, Poppins, sans-serif;
  font-size: 9px;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bulk--repost-meta {
  max-width: 100%;
  margin-top: 5px;
  overflow: hidden;
  color: var(--modal-text-muted);
  font-family: Poppins-Regular, Poppins, sans-serif;
  font-size: 10px;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bulk--repost-price {
  max-width: 100%;
  margin-top: 5px;
  overflow: hidden;
  color: var(--modal-red-dark);
  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multiselect--empty {
  display: flex;
  min-height: 110px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 20px;
  color: var(--modal-text-muted);
  font-family: Poppins-Regular, Poppins, sans-serif;
  font-size: 12px;
  line-height: 1.6;
  text-align: center;
}

.multiselect--empty-icon {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
}

/* Information */

.information--notice {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 10px;
  padding: 13px 14px;
  border: 1px solid #e2e5e9;
  border-radius: 12px;
  background: #f8f9fa;
}

.information--notice-icon {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  color: #656d76;
}

.information--notice p {
  min-width: 0;
  margin: 0;
  color: #62676d;
  font-family: Poppins-Regular, Poppins, sans-serif;
  font-size: 11px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

/* Confirmation */

.confirmation--heading {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 22px;
}

.confirmation--heading-copy {
  min-width: 0;
  flex: 1;
}

.confirmation--icon {
  display: flex;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #16794b;
  background: #eaf8f1;
}

.confirmation--heading h3 {
  margin: 0 0 4px;
  color: var(--modal-text);
  font-family: Poppins-Bold, Poppins, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.confirmation--heading p {
  margin: 0;
  color: var(--modal-text-secondary);
  font-family: Poppins-Regular, Poppins, sans-serif;
  font-size: 12px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.selected--listing-card {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 15px;
  margin-bottom: 18px;
  padding: 14px;
  overflow: hidden;
  border: 1px solid var(--modal-border);
  border-radius: 15px;
  background: #ffffff;
}

.selected--listing-image {
  width: 94px;
  height: 94px;
  flex: 0 0 94px;
  overflow: hidden;
  border-radius: 12px;
  background: #eeeeee;
}

.selected--listing-image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.selected--listing-content {
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.selected--listing-label {
  margin-bottom: 4px;
  color: var(--modal-red);
  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.selected--listing-content h4 {
  display: -webkit-box;
  overflow: hidden;
  margin: 0;
  color: var(--modal-text);
  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.selected--listing-number {
  max-width: 100%;
  margin-top: 4px;
  overflow: hidden;
  color: var(--modal-text-muted);
  font-family: Poppins-Regular, Poppins, sans-serif;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selected--listing-bottom {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  margin-top: 9px;
}

.selected--listing-bottom strong {
  min-width: 0;
  overflow: hidden;
  color: var(--modal-red-dark);
  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selected--listing-bottom span {
  max-width: 150px;
  overflow: hidden;
  padding: 4px 8px;
  border-radius: 999px;
  color: #555555;
  background: #f1f1f1;
  font-family: Poppins-Medium, Poppins, sans-serif;
  font-size: 9px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Summary */

.summary--card {
  width: 100%;
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--modal-border);
  border-radius: 15px;
  background: #ffffff;
}

.summary--header {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--modal-border);
  background: var(--modal-surface-soft);
}

.summary--header h4 {
  min-width: 0;
  margin: 0;
  color: var(--modal-text);
  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.summary--header span {
  flex: 0 0 auto;
  color: var(--modal-text-muted);
  font-family: Poppins-Regular, Poppins, sans-serif;
  font-size: 9px;
}

.summary--row {
  display: grid;
  grid-template-columns:
    minmax(0, 1fr)
    minmax(100px, auto);
  align-items: flex-start;
  gap: 20px;
  padding: 13px 16px;
}

.summary--label {
  min-width: 0;
  color: var(--modal-text-secondary);
  font-family: Poppins-Regular, Poppins, sans-serif;
  font-size: 11px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.summary--value {
  min-width: 0;
  color: var(--modal-text);
  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.5;
  text-align: right;
  overflow-wrap: anywhere;
}

.summary--row-muted .summary--value {
  color: var(--modal-text-secondary);
}

.summary--value-period {
  max-width: 250px;
}

.summary--divider {
  height: 1px;
  margin: 0 16px;
  background: var(--modal-border);
}

.confirmation--notice {
  margin-top: 16px;
  padding: 12px 14px;
  border-left: 3px solid #d2a734;
  border-radius: 5px 11px 11px 5px;
  color: #675823;
  background: #fffaf0;
  font-family: Poppins-Regular, Poppins, sans-serif;
  font-size: 10px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

/* Footer */

.modal--footer {
  position: relative;
  z-index: 4;
  display: flex;
  min-width: 0;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 18px 28px;
  border-top: 1px solid var(--modal-border);
  background: #ffffff;
  box-shadow: 0 -8px 20px rgba(17, 24, 39, 0.025);
}

.modal--button {
  display: inline-flex;
  min-width: 0;
  min-height: 46px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 0 20px;
  border-radius: 11px;
  font-family: Poppins-SemiBold, Poppins, sans-serif;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease,
    box-shadow 0.2s ease, transform 0.2s ease;
}

.modal--button:focus-visible {
  outline: 3px solid rgba(215, 25, 32, 0.15);
  outline-offset: 2px;
}

.modal--button:disabled {
  cursor: not-allowed;
  opacity: 0.52;
  transform: none;
}

.modal--button-secondary {
  border: 1px solid #d9d9d9;
  color: #444444;
  background: #ffffff;
}

.modal--button-secondary:hover:not(:disabled) {
  border-color: #bcbcbc;
  background: #f7f7f7;
}

.modal--button-primary {
  min-width: 150px;
  border: 1px solid var(--modal-red);
  color: #ffffff;
  background: var(--modal-red);
  box-shadow: 0 8px 18px rgba(215, 25, 32, 0.18);
}

.modal--button-primary:hover:not(:disabled) {
  border-color: var(--modal-red-dark);
  background: var(--modal-red-dark);
  box-shadow: 0 11px 23px rgba(173, 17, 23, 0.24);
  transform: translateY(-1px);
}

/* Tablet */

@media (max-width: 767px) {
  ::v-deep .listing--form-modal .vm--modal {
    top: 8px !important;
    left: 8px !important;
    width: calc(100vw - 16px) !important;
    max-width: calc(100vw - 16px) !important;
    height: calc(100vh - 16px) !important;
    height: calc(100dvh - 16px) !important;
    max-height: calc(100vh - 16px) !important;
    max-height: calc(100dvh - 16px) !important;
    margin: 0 !important;
    border-radius: 18px;
  }

  .modal--wrapper {
    width: 100%;
    height: 100%;
    max-height: none;
    border-radius: 18px;
  }

  .modal--header {
    gap: 12px;
    padding: 18px 18px 16px;
  }

  .modal--header-content {
    gap: 12px;
    padding-right: 38px;
  }

  .modal--header-icon {
    width: 42px;
    height: 42px;
    flex-basis: 42px;
    border-radius: 12px;
  }

  .modal--eyebrow {
    font-size: 9px;
  }

  .modal--title {
    font-size: 18px;
    line-height: 1.35;
    letter-spacing: -0.2px;
  }

  .modal--description {
    margin-top: 4px;
    font-size: 11px;
    line-height: 1.55;
  }

  .modal--close {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 36px;
    height: 36px;
    flex-basis: 36px;
  }

  .modal--steps {
    grid-template-columns:
      minmax(0, 1fr)
      36px
      minmax(0, 1fr);
    justify-content: stretch;
    padding: 12px 18px;
  }

  .modal--step {
    gap: 8px;
  }

  .modal--step:last-child {
    justify-self: end;
  }

  .modal--step-number {
    width: 28px;
    height: 28px;
    flex-basis: 28px;
  }

  .modal--step-label {
    display: none;
  }

  .modal--step-copy strong {
    font-size: 10px;
  }

  .modal--body {
    padding: 18px 18px 22px;
  }

  .period--card {
    margin-bottom: 20px;
    padding: 12px 13px;
  }

  .period--icon {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
  }

  .period--label {
    font-size: 10px;
  }

  .period--value {
    font-size: 12px;
  }

  .listing--form-heading {
    margin-bottom: 11px;
  }

  .listing--form-label {
    font-size: 14px;
  }

  .listing--form-hint {
    font-size: 10px;
    line-height: 1.55;
  }

  .bulk--repost-input::v-deep .multiselect,
  .bulk--repost-input::v-deep .multiselect__tags {
    min-height: 96px;
  }

  .bulk--repost-input::v-deep .multiselect__tags {
    padding: 10px 44px 10px 10px;
    border-radius: 13px;
  }

  .bulk--repost-input::v-deep .multiselect__placeholder {
    min-height: 74px;
    padding: 0 8px;
    font-size: 12px;
  }

  .bulk--repost-input::v-deep .multiselect__select {
    right: 5px;
    width: 36px;
    height: 36px;
  }

  .bulk--repost-input::v-deep .multiselect__content-wrapper {
    max-height: min(240px, 38vh) !important;
    border-radius: 12px;
  }

  .bulk--repost-option {
    gap: 10px;
    padding: 3px;
  }

  .bulk--repost-img {
    width: 66px;
    height: 66px;
    flex-basis: 66px;
    border-radius: 9px;
  }

  .bulk--repost-title {
    font-size: 11px;
  }

  .bulk--repost-meta {
    font-size: 9px;
  }

  .bulk--repost-price {
    font-size: 11px;
  }

  .property--type {
    max-width: 90px;
    padding: 3px 7px;
    font-size: 8px;
  }

  .information--notice {
    padding: 12px;
  }

  .information--notice p {
    font-size: 10px;
  }

  .confirmation--heading {
    gap: 11px;
    margin-bottom: 18px;
  }

  .confirmation--icon {
    width: 38px;
    height: 38px;
    flex-basis: 38px;
  }

  .confirmation--heading h3 {
    font-size: 16px;
  }

  .confirmation--heading p {
    font-size: 10px;
  }

  .selected--listing-card {
    align-items: flex-start;
    gap: 11px;
    padding: 11px;
  }

  .selected--listing-image {
    width: 76px;
    height: 76px;
    flex-basis: 76px;
    border-radius: 10px;
  }

  .selected--listing-content h4 {
    font-size: 12px;
  }

  .selected--listing-bottom strong {
    font-size: 11px;
  }

  .summary--header {
    padding: 12px 13px;
  }

  .summary--row {
    grid-template-columns:
      minmax(0, 1fr)
      minmax(90px, auto);
    gap: 12px;
    padding: 12px 13px;
  }

  .summary--divider {
    margin: 0 13px;
  }

  .summary--value-period {
    max-width: 145px;
  }

  .modal--footer {
    gap: 10px;
    padding: 12px 18px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
  }

  .modal--button {
    min-height: 44px;
    flex: 1 1 0;
    padding: 0 12px;
    font-size: 11px;
  }

  .modal--button-primary {
    min-width: 0;
    flex-grow: 1.25;
  }
}

/* Small mobile */

@media (max-width: 480px) {
  .modal--header-icon {
    width: 38px;
    height: 38px;
    flex-basis: 38px;
  }

  .modal--header-icon svg {
    width: 21px;
    height: 21px;
  }

  .modal--header-content {
    gap: 10px;
  }

  .modal--title {
    font-size: 17px;
  }

  .modal--description {
    font-size: 10px;
  }

  .modal--steps {
    grid-template-columns:
      minmax(0, 1fr)
      24px
      minmax(0, 1fr);
    padding-right: 16px;
    padding-left: 16px;
  }

  .modal--step {
    gap: 7px;
  }

  .modal--step-copy strong {
    white-space: normal;
  }

  .modal--body {
    padding-right: 16px;
    padding-left: 16px;
  }

  .property--type {
    display: none;
  }

  .bulk--repost-img {
    width: 62px;
    height: 62px;
    flex-basis: 62px;
  }

  .bulk--repost-input::v-deep .multiselect,
  .bulk--repost-input::v-deep .multiselect__tags {
    min-height: 88px;
  }

  .bulk--repost-input::v-deep .multiselect__placeholder {
    min-height: 66px;
  }

  .selected--listing-bottom {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .selected--listing-bottom span {
    max-width: 100%;
  }

  .summary--header span {
    display: none;
  }
}

/* Very small mobile */

@media (max-width: 359px) {
  ::v-deep .listing--form-modal .vm--modal {
    top: 4px !important;
    left: 4px !important;
    width: calc(100vw - 8px) !important;
    max-width: calc(100vw - 8px) !important;
    height: calc(100vh - 8px) !important;
    height: calc(100dvh - 8px) !important;
    max-height: calc(100vh - 8px) !important;
    max-height: calc(100dvh - 8px) !important;
  }

  .modal--header {
    padding-right: 14px;
    padding-left: 14px;
  }

  .modal--header-content {
    padding-right: 34px;
  }

  .modal--header-icon {
    display: none;
  }

  .modal--close {
    right: 12px;
  }

  .modal--steps {
    padding-right: 14px;
    padding-left: 14px;
  }

  .modal--step-copy {
    display: none;
  }

  .modal--step:first-child {
    justify-self: start;
  }

  .modal--step:last-child {
    justify-self: end;
  }

  .modal--body {
    padding-right: 14px;
    padding-left: 14px;
  }

  .bulk--repost-img {
    width: 58px;
    height: 58px;
    flex-basis: 58px;
  }

  .selected--listing-image {
    width: 68px;
    height: 68px;
    flex-basis: 68px;
  }

  .summary--row {
    grid-template-columns:
      minmax(0, 1fr)
      minmax(74px, auto);
    gap: 8px;
  }

  .modal--footer {
    flex-direction: column-reverse;
    padding-right: 14px;
    padding-left: 14px;
  }

  .modal--button {
    width: 100%;
    flex: 0 0 auto;
  }
}

/* Short-height devices */

@media (max-height: 620px) and (max-width: 767px) {
  .modal--header {
    padding-top: 13px;
    padding-bottom: 12px;
  }

  .modal--header-icon {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
  }

  .modal--description {
    display: none;
  }

  .modal--steps {
    padding-top: 9px;
    padding-bottom: 9px;
  }

  .modal--body {
    padding-top: 14px;
    padding-bottom: 16px;
  }

  .period--card {
    margin-bottom: 14px;
  }

  .listing--form-heading {
    margin-bottom: 8px;
  }

  .modal--footer {
    padding-top: 9px;
  }
}
</style>
